import React, { ComponentType, ReactNode, RefObject } from 'react';
import { v4 as uuid } from 'uuid';
import {
  AutoSuggest,
  Option,
} from '@teetsh/app/src/components/Form/AutoSuggest';
import {
  removeItemAtIndex,
  replaceItemAtIndex,
} from '@teetsh/app/src/utils/listMethods';
import Button from '@teetsh/app/src/components/Buttons/Button';
import tw from 'twin.macro';
import Tooltip2 from '@teetsh/app/src/components/Tooltip2';
import FloppyDisk from '@teetsh/app/src/components/Icons/icons/FloppyDisk';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Item } from './types';
import { AutoSuggestGroupContext } from './useAutoSuggestGroupContext';
import { useTranslation } from 'react-i18next';

interface Props {
  className?: string;
  helperText?: React.ReactNode;
  placeholder?: string;
  onSave?: (v: string) => void;
  label?: string;
  align?: 'center' | 'end';
  options: Option[];
  onChange: (v: Item[]) => void;
  singleValue?: boolean;
  items: Item[];
  inputRef?: RefObject<HTMLInputElement>;
  Component?: ComponentType<any>;
  children?: ReactNode;
}

const alignItems = {
  center: tw`items-center`,
  end: tw`items-end`,
};

export default function AutoSuggestGroup(props: Props) {
  const {
    label,
    options,
    placeholder,
    className = '',
    onSave,
    helperText,
    align = 'end',
    items,
    singleValue,
    onChange,
    inputRef,
    Component,
    children,
  } = props;
  const { t } = useTranslation(['common']);

  return (
    <>
      {' '}
      {(items || []).map((item, index) => {
        const removeButton = (
          <Button
            aria-label={`Supprimer ${item.value}`}
            minimal
            key={`remove_${item.id}`}
            intent="danger"
            onClick={() => onChange(removeItemAtIndex<Item>(items, index))}
            icon={TrashIcon}
          />
        );

        const itemValue = item.value;
        const itemKey = item.id;

        const addButton = (
          <Button
            minimal
            key={`add_${item.id}`}
            aria-label={`Ajouter un élément à ${label}`}
            onClick={() => {
              onChange([...items, { value: '', id: uuid() }]);
              setTimeout(() => {
                inputRef?.current?.focus();
              }, 1);
            }}
            icon={PlusIcon}
          />
        );

        const saveButton =
          onSave &&
          itemValue !== '' &&
          !options.map(o => o.value).includes(itemValue) ? (
            <Tooltip2 label={t('common:components.autosuggest.saveInMyList')}>
              <Button
                minimal
                key={`add_${item.id}`}
                onClick={e => {
                  e.stopPropagation();
                  onSave(item.value);
                }}
                aria-label={`Enregistrer ${item.value}`}
                icon={FloppyDisk as any}
              />
            </Tooltip2>
          ) : null;

        return (
          <div
            key={itemKey}
            css={[tw`relative mb-1 flex gap-x-2`, alignItems[align]]}
          >
            <AutoSuggestGroupContext.Provider
              value={{
                item,
              }}
            >
              <div tw="flex-grow">
                <AutoSuggest
                  key={itemKey}
                  onChange={newValue =>
                    onChange(
                      replaceItemAtIndex(
                        items,
                        { ...item, value: newValue },
                        index,
                      ),
                    )
                  }
                  onEnterKeyPress={
                    singleValue
                      ? () => {}
                      : () => onChange([...items, { value: '', id: uuid() }])
                  }
                  id={itemKey}
                  inputRef={inputRef}
                  Component={Component}
                  value={itemValue}
                  label={index === 0 ? label : ''}
                  helperText={helperText}
                  placeholder={placeholder}
                  options={options}
                  className={className}
                />
              </div>
              {saveButton}
              {!singleValue && addButton}
              {removeButton}
              {children}
            </AutoSuggestGroupContext.Provider>
          </div>
        );
      })}
    </>
  );
}
